import { useState } from 'react'
import { useApi } from '../../../hooks/useApi'
import { useFetchState, Status } from '../../../hooks/useFetchState'
import { RelocateApiService } from '../services/relocateApi'
import { GetFirstPositionResponse, ValidatePositionResponse } from '../types'
import { eTypePosition } from '../../../enums/eTypePosition'
import { checkIfCommentsRequired } from '../utils/relocateUtils'

interface UseDamageAssessmentProps {
  sharedState: {
    sku: string
    productOriginId: number | null
    originZone?: { zoneId: number } | null
    originPositionType?: number
  }
  setSharedState: (updater: (prev: any) => any) => void
  onDamageResponseComplete: (wasAutoAssigned: boolean) => void
  onDestinyAutoAssigned: (positionCode: string) => void
  onFocusDocumentNumber?: () => void
  relocateCommentaryEnabled: boolean
}

interface UseDamageAssessmentReturn {
  showMermaQuestion: boolean
  setShowMermaQuestion: (value: boolean) => void
  isAutoAssignedPosition: boolean
  setIsAutoAssignedPosition: (value: boolean) => void
  autoAssignmentFailed: boolean
  qualityPositionState: {
    loading: boolean
    success: boolean
    error: string | null
  }
  handleMermaResponse: (isDamaged: boolean) => Promise<void>
}

export const useDamageAssessment = ({
  sharedState,
  setSharedState,
  onDamageResponseComplete,
  onDestinyAutoAssigned,
  onFocusDocumentNumber,
  relocateCommentaryEnabled
}: UseDamageAssessmentProps): UseDamageAssessmentReturn => {
  const [showMermaQuestion, setShowMermaQuestion] = useState(false)
  const [isAutoAssignedPosition, setIsAutoAssignedPosition] = useState(false)
  const [autoAssignmentFailed, setAutoAssignmentFailed] = useState(false)

  const { callApi: firstPositionCallApi } = useApi<GetFirstPositionResponse>()
  const { callApi: validatePositionCallApi } = useApi<ValidatePositionResponse>()

  const {
    state: qualityState,
    setLoading: setQualityLoading,
    setSuccess: setQualitySuccess,
    setError: setQualityError
  } = useFetchState()

  const handleMermaResponse = async (isDamaged: boolean) => {
    setSharedState(prev => ({ ...prev, isDamaged }))

    let wasAutoAssigned = false

    if (isDamaged) {
      try {
        setQualityLoading()

        // Obtener primera posición de calidad transferencia disponible
        const positionsResponse = await firstPositionCallApi(RelocateApiService.getFirstPositionByType(
          sharedState.originZone!.zoneId,
          eTypePosition.CalidadTransferencia
        ))

        const positions = positionsResponse?.items || []

        if (positions.length > 0) {
          const qualityPosition = positions[0]
          const positionCode = qualityPosition.positionCode

          // Validar la posición obtenida
          const response = await validatePositionCallApi(RelocateApiService.validateDestinyPosition(
            positionCode,
            sharedState.sku,
            sharedState.productOriginId!
          ))

          if (response) {
            setAutoAssignmentFailed(false)
            const destinyTypeId = response.typePositionId
            const originTypeId = sharedState.originPositionType
            const requiresComments = relocateCommentaryEnabled && checkIfCommentsRequired(originTypeId, destinyTypeId)

            setSharedState(prev => ({
              ...prev,
              posicionDestino: positionCode,
              destinyPositionId: response.positionId,
              destinyPositionType: destinyTypeId,
              requiresComments: Boolean(requiresComments)
            }))
            setIsAutoAssignedPosition(true)
            wasAutoAssigned = true

            // Notificar que la posición fue auto-asignada
            onDestinyAutoAssigned(positionCode)

            // Si requiere comentarios, hacer focus en el campo de documento
            if (requiresComments && onFocusDocumentNumber) {
              onFocusDocumentNumber()
            }
          } else {
            setAutoAssignmentFailed(true)
          }
        } else {
          setAutoAssignmentFailed(true)
        }

        setQualitySuccess()
      } catch (error) {
        setQualityError('Error obteniendo posición de calidad. Intente de nuevo.')
      }
    } else {
      // Reset destino si no está mermado
      setSharedState(prev => ({
        ...prev,
        posicionDestino: '',
        destinyPositionId: undefined,
        destinyPositionType: undefined
      }))
    }

    setShowMermaQuestion(false)
    onDamageResponseComplete(wasAutoAssigned)
  }

  return {
    showMermaQuestion,
    setShowMermaQuestion,
    isAutoAssignedPosition,
    setIsAutoAssignedPosition,
    autoAssignmentFailed,
    qualityPositionState: {
      loading: qualityState.status === Status.Loading,
      success: qualityState.status === Status.Success,
      error: qualityState.error || null
    },
    handleMermaResponse
  }
}